import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Copy, DollarSign, Users, Gift, ChevronDown, ChevronUp } from "lucide-react";

const TYPE_LABELS = { signup: "Signup", membership: "Membership", store: "Store", booking: "Session", pass: "Pass", league: "League" };

export default function AffiliateCard() {
  const [data, setData] = useState(null);
  const [joining, setJoining] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/affiliate/me");
      setData(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const join = async () => {
    setJoining(true);
    try {
      await api.post("/affiliate/join");
      toast.success("You're in! Share your code and start earning.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not join"); }
    finally { setJoining(false); }
  };

  if (!data) return null;
  const shareLink = data.code ? `${window.location.origin}/auth?mode=register&ref=${data.code}` : "";
  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  return (
    <div className="bg-pb-surface border border-pb-border p-6 mb-8" data-testid="affiliate-card">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="w-5 h-5 text-pb-red" />
        <span className="font-display font-bold text-lg uppercase">Affiliate Program</span>
      </div>
      {!data.joined ? (
        <div>
          <p className="text-sm text-zinc-400 mb-4">Refer friends to the Bullpen and earn rewards on their memberships, store purchases, sessions and league entries. Get your unique code instantly.</p>
          <button data-testid="affiliate-join-btn" onClick={join} disabled={joining}
            className="bg-pb-red text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
            {joining ? "Joining…" : "Become an Affiliate"}
          </button>
        </div>
      ) : !data.active ? (
        <p className="text-sm text-amber-400" data-testid="affiliate-suspended">Your affiliate account is currently suspended. Contact the Bullpen team for details.</p>
      ) : (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="bg-pb-elevated border border-pb-cyan/40 px-4 py-2 font-mono text-lg text-pb-cyan" data-testid="affiliate-code">{data.code}</div>
            <button data-testid="affiliate-copy-code" onClick={() => copy(data.code, "Code")}
              className="flex items-center gap-1.5 border border-pb-border hover:border-pb-red hover:text-pb-red text-xs font-bold uppercase tracking-widest px-3 py-2.5 transition-colors">
              <Copy className="w-3.5 h-3.5" /> Code
            </button>
            <button data-testid="affiliate-copy-link" onClick={() => copy(shareLink, "Share link")}
              className="flex items-center gap-1.5 border border-pb-border hover:border-pb-red hover:text-pb-red text-xs font-bold uppercase tracking-widest px-3 py-2.5 transition-colors">
              <Copy className="w-3.5 h-3.5" /> Share Link
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat icon={Users} label="Referrals" value={data.signups} testid="affiliate-referrals" />
            <Stat icon={DollarSign} label="Pending" value={`$${data.pending_cash.toFixed(2)}`} testid="affiliate-pending" />
            <Stat icon={DollarSign} label="Paid Out" value={`$${data.paid_cash.toFixed(2)}`} testid="affiliate-paid" />
            <Stat icon={Gift} label="Store Credit" value={`$${data.store_credit.toFixed(2)}`} testid="affiliate-credit" />
          </div>
          {data.recent.length > 0 && (
            <div>
              <button data-testid="affiliate-history-toggle" onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">
                {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                Recent activity ({data.recent.length})
              </button>
              {showHistory && (
                <div className="mt-3 divide-y divide-pb-border/40 border border-pb-border/60">
                  {data.recent.map((c, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-xs" data-testid="affiliate-conversion-row">
                      <span className="text-zinc-300">{TYPE_LABELS[c.type] || c.type} — {c.referred_name}</span>
                      <span className="font-mono">
                        {c.commission > 0 && <span className={c.status === "paid" ? "text-pb-red" : "text-amber-400"}>${c.commission.toFixed(2)} {c.status}</span>}
                        {c.credit > 0 && <span className="text-pb-cyan ml-2">+${c.credit.toFixed(2)} credit</span>}
                        {c.commission === 0 && c.credit === 0 && <span className="text-zinc-600">—</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-pb-elevated border border-pb-border p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1"><Icon className="w-3 h-3" />{label}</div>
      <div className="font-display font-black text-lg text-pb-red">{value}</div>
    </div>
  );
}
